import { eq, isNil, map, compose, keyBy, mapValues, get, over, reduce, uniqBy } from 'lodash/fp';
import { createSelector } from 'reselect';
import { pipe, flow } from 'fp-ts/function';
import { array, option, record } from 'fp-ts';
import { getProp, getStringProp } from '@mews/fp';
import { Service } from '@mews-commander/data-access';
import { composeProps, createPropsExtender, createScopedSelector, matchTimeUnitKey } from '@mews/framework';
import { isDayUse } from '@mews-commander/utils';
import { getServicesByIds } from '@mews-commander/legacy-entities/src/ducks/services';
import { isBookableService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { getResourceCategoryIdsByResourceCategoryTypeName } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { moduleSelector } from '../module';
import { createDateRangeKey } from '../utils/keys';
import { INITIAL_STATE } from '../../search/reducer';
import { getSelectedServiceCategories, getSelectedServiceAgeCategories } from './entities';
import { getSelectedResourceCategoryTypeName } from './screens';

const getOrderScopes = createSelector(moduleSelector, get('order'));
export const getOrder = createScopedSelector({ parentSelector: getOrderScopes, initialState: INITIAL_STATE });

export const getOrderValue = (key: string) =>
    flow(
        getOrder,
        record.lookup(key),
        option.getOrElseW(() => undefined)
    );

export const getStartDate = createSelector(getOrder, (o) => o.startDate);
export const getEndDate = createSelector(getOrder, (o) => o.endDate);

export const getStartTime = createSelector(getOrder, (o) => o.startTime);
export const getEndTime = createSelector(getOrder, (o) => o.endTime);

export const hasTimesSelected = createSelector(
    getStartTime,
    getEndTime,
    (startTime, endTime) => !isNil(startTime) && !isNil(endTime)
);

export const getDateRangeKeyFromOrder = createSelector(getOrder, createDateRangeKey);

export const sameDayOrder = createSelector(getStartDate, getEndDate, (startDate, endDate) =>
    isNil(startDate) ? false : eq(startDate, endDate)
);

export const hasDatesSelected = createSelector(
    getStartDate,
    getEndDate,
    (startDate, endDate) => !isNil(startDate) && !isNil(endDate)
);

export const getVoucherId = createSelector(getOrder, (order) => order.voucherId);
export const getBusinessSegmentId = createSelector(getOrder, (order) => order.businessSegmentId || '');
export const getProductIds = createSelector(getOrder, (order) => order.productIds || []);
export const getOccupancies = createSelector(getOrder, (order) => order.occupancies || []);
export const getChannelNumber = createSelector(getOrder, (order) => order.channelNumber);
export const getPurpose = createSelector(getOrder, (order) => order.purpose);

export const getBusinessSegmentIdOrDefault = createSelector(getBusinessSegmentId, (id) => id || '');
export const getVoucherIdOrDefault = createSelector(getVoucherId, (id) => id || '');
export const getChannelNumberOrDefault = createSelector(getChannelNumber, (number) => number || '');
export const getReservationPurposeOrDefault = createSelector(getPurpose, (purpose) => purpose || '');

export const getCategoryOccupancy = createSelector(
    getOccupancies,
    getStringProp('categoryId'),
    (occupancies, categoryId) => occupancies[categoryId] || {}
);

export const getCategoryOccupancyCount = compose(get('count'), getCategoryOccupancy);

export const getCategoryOccupancyTotalCount = createSelector(getCategoryOccupancy, ({ occupancyData = {} }) =>
    reduce((acc, { personCount }: { personCount: number }) => acc + personCount, 0)(occupancyData)
);

const getOccupancyData = (defaultPersonCount: $TSFixMe, ageCategories: $TSFixMe) =>
    keyBy(
        'ageCategoryId',
        mapValues(
            ({ id, isDefault }) => ({
                ageCategoryId: id,
                personCount: isDefault ? defaultPersonCount : 0,
            }),
            ageCategories
        )
    );
export const getDefaultCategoryOccupancies = compose(
    ([resourceCategories, ageCategories]) =>
        mapValues(
            ({ id, capacity }) => ({
                adultCount: capacity,
                childCount: 0,
                categoryId: id,
                count: 1,
                occupancyData: getOccupancyData(capacity, ageCategories),
            }),
            keyBy('id', resourceCategories)
        ),
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    over([getSelectedServiceCategories, getSelectedServiceAgeCategories])
);

export const getCategoryOccupancies = createSelector(
    getOccupancies,
    composeProps(
        getResourceCategoryIdsByResourceCategoryTypeName,
        createPropsExtender('resourceCategoryTypeName', getSelectedResourceCategoryTypeName)
    ),
    (occupancies, resourceCategoryIds) =>
        pipe(
            resourceCategoryIds,
            array.filterMap((resourceCategoryId) => pipe(occupancies, record.lookup(resourceCategoryId)))
        )
);

const getGroupReservations = createSelector(getProp()('dateGroup'), get('reservations'));

export const getGroupReservationServiceIds = createSelector(getGroupReservations, map('serviceId'));

export const getTimeUnitCountConfigutations = createSelector(
    getServicesByIds,
    compose(
        uniqBy('timeUnitLabelKey'),
        // @ts-expect-error $TSFixMeError: No overload matches this call.
        map((service: Service) => {
            const isServiceDayUse = isDayUse(service);
            const timeUnit = isBookableService(service) && service.data.value?.timeUnitPeriod;
            return {
                timeUnit,
                timeUnitLabelKey: timeUnit && matchTimeUnitKey({ plural: true, isServiceDayUse, timeUnit }),
                isDayUse: isServiceDayUse,
            };
        })
    )
);
